import React, { useCallback, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import toast from "react-hot-toast";
// import { IconX } from "@tabler/icons-react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  UseFormClearErrors,
  Control,
  UseFormSetError,
} from "react-hook-form";
import SelectInput from "../../../components/Select";
import Input from "../../../components/Input";
import ToggleButton from "../../../components/ToggleButton";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import { OrderFormData, LocationState, AddressOption } from "../../../types/types";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
  useDistrictFetchData,
} from "../../../apis/locationApis";
import SenderAddress from "./SenderAddress";

interface SenderSectionProps {
  clients: any[];
  register: UseFormRegister<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  clearErrors: UseFormClearErrors<OrderFormData>;
  watch: any;
  useDifferentSender: boolean;
  setUseDifferentSender: (value: boolean) => void;
  onClientSelect: (clientId: string) => void;
  control: Control<OrderFormData>;
  setError: UseFormSetError<OrderFormData>;
}

const SenderSection: React.FC<SenderSectionProps> = ({
  clients,
  register,
  errors,
  setValue,
  clearErrors,
  watch,
  useDifferentSender,
  setUseDifferentSender,
  onClientSelect,
  control,
  setError,
}) => {
  const { t } = useLanguage();
  const [senderLocation, setSenderLocation] = React.useState<LocationState>({});
  const selectedClientId = watch("clientId");
  const selectedClient = clients?.find((c: any) => c?.id === selectedClientId);
  const isNippcoMerchant = selectedClient?.isNippco === true;

  const handleToggleSender = () => {
    if (isNippcoMerchant) {
      toast.error(t("For nippco merchant orders alternate address is mandatory"));
      return;
    }
    setUseDifferentSender(!useDifferentSender);
  };
  const [searchRef, setSearchRef] = useState<string>("");
  const [districtSearchRef, setDistrictSearchRef] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<AddressOption>("short");

  const countryWatch = watch("sender.country");
  const countryNameWatch = watch("sender.countryName");
  const cityWatch = watch("sender.city");
  const cityNameWatch = watch("sender.cityName");
  const districtWatch = watch("sender.district");
  const districtNameWatch = watch("sender.districtName");

  React.useEffect(() => {
    setSenderLocation({
      country: countryWatch ? { id: countryWatch, name: countryNameWatch || "" } : undefined,
      city: cityWatch ? { id: cityWatch, name: cityNameWatch || "" } : undefined,
      district: districtWatch ? { id: districtWatch, name: districtNameWatch || "" } : undefined,
    });
  }, [countryWatch, countryNameWatch, cityWatch, cityNameWatch, districtWatch, districtNameWatch]);
  const {
    data: senderCities = [],
    refetch: fetchSenderCities,
    isPending: isCitiesFetchPending,
  } = useFetchCountryCitiesAllData(
    senderLocation?.country?.id,
    searchRef,
    selectedOption
  );

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) {
      setSearchRef(query);
      fetchSenderCities(); // 🔹 trigger API
    }
  };

  // API data
  const { data: countries = [] } = useFetchCountriesAllData();
  const {
    data: senderDistricts = [],
    refetch: fetchSenderDistricts,
    isPending: isDistrictFetchPending,
  } = useDistrictFetchData(
    senderLocation?.city?.id,
    districtSearchRef,
    selectedOption
  );

  const handleLocationChange = (field: keyof LocationState, value: any) => {
    if (!value?.id) return;

    setSenderLocation((prev) => {
      const newLocation = { ...prev, [field]: value };

      if (field === "country") {
        newLocation.city = undefined;
        newLocation.district = undefined;
        setValue("sender.city", "");
        setValue("sender.cityName", "");
        setValue("sender.district", "");
        setValue("sender.districtName", "");
      } else if (field === "city") {
        newLocation.district = undefined;
        setValue("sender.district", "");
        setValue("sender.districtName", "");
      }

      return newLocation;
    });

    setValue(`sender.${field}`, value.id);
    setValue(`sender.${field}Name`, value.name);
    setValue("sender.dialCode", value?.dialCode);

    if (field === "country" && value.id) {
      fetchSenderCities();
    } else if (field === "city" && value.id) {
      fetchSenderDistricts();
    }
  };

  const handleCityChange = useCallback(
    (val: string) => {
      const city = senderCities?.find((c: any) => c.id === val);
      if (!city) return;

      setValue("sender.city", city.id);
      setValue("sender.cityName", city.name);
      setSenderLocation((prev) => ({ ...prev, city, district: undefined }));
      setValue("sender.district", "");

      if (city.id) {
        fetchSenderDistricts();
      }
    },
    [senderCities, setValue, fetchSenderDistricts]
  );

  const handleDistrictSearch = (query: string) => {
    if (query.trim().length >= 3) {
      setDistrictSearchRef(query);
      fetchSenderDistricts(); // 🔹 trigger API
    }
  };

  const handleDistrictChange = useCallback(
    (val: string) => {
      const district = senderDistricts.find((d: any) => d.id === val);
      if (!district) return;

      setValue("sender.district", district.id);
      setValue("sender.districtName", district.name);
      setSenderLocation((prev) => ({ ...prev, district }));
    },
    [senderDistricts, setValue]
  );

  return (
    <div className="col-span-1 ">
      <div className="col-span-6 flex items-center space-x-4 justify-between">
        <p className="text-black font-medium text-base uppercase">
          Sender Details
        </p>
        <div className="flex justify-start items-center gap-1">
          <ToggleButton
            onChange={handleToggleSender}
            checked={useDifferentSender}
          />
          <p className="text-[#1E293B] text-sm">Send form different address</p>
        </div>
      </div>

      {!useDifferentSender && (
        <SelectInput
          className="col-span-2"
          name="clientId"
          label="Select Client"
          placeholder="Name"
          selectOptions={
            clients?.map((client) => ({
              id: client?.id,
              value: client?.id,
              text: client?.name,
            })) || []
          }
          clearErrors={clearErrors}
          register={register}
          validation={{ required: "Client is required" }}
          error={errors.clientId}
          disabled={!clients || clients.length === 0}
          value={watch("clientId")}
          setValue={onClientSelect}
        />
      )}

      {useDifferentSender && (
        <div className="mt-4 grid grid-cols-6 gap-2">
          {/* Contact Info */}
          <SelectInput
            name="sender.country"
            className="col-span-6"
            label="Country"
            placeholder="Select Country"
            selectOptions={countries.map((country: any) => ({
              id: country.id,
              value: country.id,
              text: country.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: "Sender country is required" }}
            error={errors.sender?.country}
            disabled={!countries.length}
            value={senderLocation.country?.id}
            setValue={(val: string) => {
              const country = countries.find((c: any) => c.id === val);
              handleLocationChange("country", country);
            }}
          />

          <Input
            name="sender.name"
            type="text"
            title="Name"
            register={register}
            error={errors.sender?.name}
            validation={{ required: "Sender name is required" }}
            className="col-span-2"
          />

          <div className="col-span-2">
            <label className="text-[#1E293B] text-sm whitespace-nowrap">
              Mobile<span className="text-red-600 ml-1">*</span>
            </label>
            <div
              className={`flex items-center rounded-full bg-white px-2 mt-1 border ${errors.sender?.mobile ? "border-red-500" : "border-[#D8D8D8]"
                }`}
            >
              {/* Dial code box */}
              <span className="pr-1 text-gray-700 text-sm whitespace-nowrap">
                {watch("sender.dialCode")
                  ? `+${watch("sender.dialCode")}`
                  : "+--"}
              </span>
              <div className="flex-1 border-l-2">
                <Input
                  name="sender.mobile"
                  type="number"
                  register={register}
                  error={errors.sender?.mobile}
                  hideError={true}
                  validation={{
                    required: "Sender mobile is required",
                    validate: (value: string) => {
                      const trimmed = value?.toString().trim();
                      if (trimmed.length !== 9 && trimmed.length !== 10) {
                        return "Mobile number must be 9 or 10 digits";
                      }
                      return true;
                    },
                  }}
                  className="col-span-2"
                  inputClassName="w-full border-0 outline-none bg-transparent px-2 py-[6px] text-[#1E1E1E] hide-spinner"
                />
              </div>
            </div>
            {errors.sender?.mobile && (
              <FormErrorMessage error={errors.sender?.mobile} />
            )}
          </div>

          <Input
            name="sender.email"
            type="text"
            title="Email"
            register={register}
            error={errors.sender?.email}
            validation={{
              required: "Sender email is required",
              setValueAs: (value: string) => value?.toLowerCase(),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            }}
            className="col-span-2"
          />

          <SenderAddress
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
            clearErrors={clearErrors}
            control={control}
            setError={setError}
            className="col-span-6"
            cities={senderCities}
            handleCitySearch={handleCitySearch}
            isCitiesFetchPending={isCitiesFetchPending}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            countries={countries}
            onCountryChange={(countryId: string) => {
              const country = countries.find((c: any) => c.id === countryId);
              handleLocationChange("country", country);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SenderSection;
